import { useMemo } from 'react';
import union from 'lodash/union';
import {
	type Operand,
	CLAUSE_TYPE_TERMINAL,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_LIST,
	OPERAND_TYPE_VALUE,
	type TerminalClause,
	type CompoundClause,
} from '@atlaskit/jql-ast';
import { fg } from '@atlassian/jira-feature-gating';
import { useClauseMap, useJQL } from '../index.tsx';
import { UnsupportedRefinementError } from '../unsupported-refinement-error/index.tsx';
import { UnsupportedRefinementErrorOld } from '../unsupported-refinement-error-old/index.tsx';

const getOperands = (clauseName: string, operand: Operand, jql: string): string[] => {
	switch (operand?.operandType) {
		case OPERAND_TYPE_KEYWORD:
		case OPERAND_TYPE_VALUE: {
			return [operand.value];
		}
		case OPERAND_TYPE_LIST: {
			return union(
				...operand.values.map((operandValue) => getOperands(clauseName, operandValue, jql)),
			);
		}
		default: {
			if (fg('filter_refinement_error_logging')) {
				throw new UnsupportedRefinementError(
					jql,
					`Unsupported operand type (${operand?.operandType}) for ${clauseName}`,
				);
			} else {
				throw new UnsupportedRefinementErrorOld(
					`Unsupported operand type (${operand?.operandType}) for ${clauseName}`,
				);
			}
		}
	}
};

export const getSelectedValuesForField = (
	clauses: (TerminalClause | CompoundClause)[],
	clauseName: string,
	jql: string,
) => {
	if (!clauses || clauses.length === 0) {
		return [];
	}

	if (clauses.length > 1) {
		if (fg('filter_refinement_error_logging')) {
			throw new UnsupportedRefinementError(jql, `Too many clauses for ${clauseName}`);
		} else {
			throw new UnsupportedRefinementErrorOld(`Too many clauses for ${clauseName}`);
		}
	}

	const [clause] = clauses;

	if (clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
		if (fg('filter_refinement_error_logging')) {
			throw new UnsupportedRefinementError(
				jql,
				`Unsupported clause type (${clause.clauseType}) for ${clauseName}`,
			);
		} else {
			throw new UnsupportedRefinementErrorOld(
				`Unsupported clause type (${clause.clauseType}) for ${clauseName}`,
			);
		}
	}

	const { operand } = clause;

	if (!operand) {
		if (fg('filter_refinement_error_logging')) {
			throw new UnsupportedRefinementError(jql, `No operand for ${clauseName}`);
		} else {
			throw new UnsupportedRefinementErrorOld(`No operand for ${clauseName}`);
		}
	}

	return getOperands(clauseName, operand, jql);
};

export const useSelectedValues = (clauseName: string): string[] => {
	const clauseMap = useClauseMap();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const jql = fg('filter_refinement_error_logging') ? useJQL() : '';

	const clauses = clauseMap[clauseName.toLowerCase()];

	return useMemo(() => {
		return getSelectedValuesForField(clauses, clauseName, jql);
	}, [clauses, clauseName, jql]);
};

export const useNumberOfSelectedValues = () => {
	const clauseMap = useClauseMap();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const jql = fg('filter_refinement_error_logging') ? useJQL() : '';

	return useMemo(() => {
		let numberOfSelectedValues = 0;
		const clauseName = Object.keys(clauseMap);

		clauseName.forEach((name) => {
			const clauses = clauseMap[name];
			const fieldValues = getSelectedValuesForField(clauses, name, jql);
			numberOfSelectedValues += fieldValues.length;
		});

		return numberOfSelectedValues;
	}, [clauseMap, jql]);
};
